import axios from "axios";
import { apiEndpoint, apiEndpointFunction } from '@iso/lib/helpers/endpoint';
import { authHeader } from '@iso/lib/helpers/authHeader';
import qs from 'qs';

export const assetService = {
  getList,
  getMapMarkers,
  storeOrUpdate,
  destroy,
  getAnimalSire,
  getByPrimaryObject,
  lockOut,
  updateLockOut,
  unlock,
  assignUser,
  unassignUser,
  setExpiry,
  getTimeline,
  getOverview,
  exportServiceHistory,
  emailServiceHistory,
  resolveQr,
};

// Aggregated Overview payload for the passport's Overview tab.
// Backend: GET /api/web/facilities/{id}/overview.
function getOverview(assetId) {
  return axios
    .get(`${apiEndpointFunction.facilities(assetId)}/overview`, { headers: authHeader() })
    .then(res => (res && res.status === 200 && res.data.code === '0000' ? res.data : {}));
}

// Resolve a scanned QR token back to its asset (for the scan-to-passport flow).
// Property-scoped server-side, so this only returns assets the user can access.
// Backend: GET /api/web/facilities/resolve-qr?token=.
function resolveQr(token) {
  return axios
    .get(`${apiEndpoint.facilities}/resolve-qr`, {
      headers: authHeader(),
      params: { token },
    })
    .then(res => (res && res.status === 200 ? res.data : {}));
}

// Download the branded service-history PDF (optional {from,to} ISO dates).
// Resolves to a Blob the caller can save.
function exportServiceHistory(assetId, params = {}) {
  const queryString = qs.stringify(params, { addQueryPrefix: true });
  return axios
    .get(`${apiEndpointFunction.facilities(assetId)}/service-history-report${queryString}`, {
      headers: authHeader(),
      responseType: 'blob',
    })
    .then(res => res.data);
}

// Email the service-history PDF. payload: { from?, to?, recipient_emails: [] }.
function emailServiceHistory(assetId, payload) {
  return axios
    .post(`${apiEndpointFunction.facilities(assetId)}/email-service-history`, payload, { headers: authHeader() })
    .then(res => (res && res.status === 200 ? res.data : {}));
}

// Merged single-machine timeline (service logs + inspections + activity) plus
// an asset stats block. Backend: GET /api/web/facilities/{id}/timeline.
function getTimeline(assetId) {
  return axios
    .get(`${apiEndpointFunction.facilities(assetId)}/timeline`, { headers: authHeader() })
    .then(res => (res && res.status === 200 && res.data.code === '0000' ? res.data : {}));
}

function getList(propertyId, params = {}) {
  const queryString = qs.stringify({property_id: propertyId, ...params}, { addQueryPrefix: true });
  return (
    axios
      .get(
        `${apiEndpoint.facilities}${queryString}`,
        {
          headers: authHeader(),
        }
      ).then(res => {
        if (res && res.status === 200 && res.data.code === '0000') {
          return res.data;
        }
      })
  );
}

function getMapMarkers(propertyId) {
  return axios
    .get(`${apiEndpoint.facilities}/map-markers?property_id=${propertyId}`, {
      headers: authHeader(),
    })
    .then(res => {
      if (res && res.status === 200 && res.data.code === '0000') {
        return res.data;
      }
    });
}

function getByPrimaryObject(primaryObjectId){
  return (
    axios
      .get(
        `${apiEndpoint.facilities}/get-by-primary-object?primary_object_id=${primaryObjectId}`,
        {
          headers: authHeader(),
        }
      ).then(res => {
        if(res && res.status === 200 && res.data.code === '0000'){
          return res.data;
        }
      })
  );
}

function storeOrUpdate(data, id = null){
  return (id ? (
    axios
      .put(
        apiEndpointFunction.facilities(id),
        data,
        {
          headers: authHeader(),
        }
      )
  ) : (
    axios
      .post(
        apiEndpoint.facilities,
        data,
        {
          headers: authHeader()
        }
      )
  )).then(res => {
    if(res && res.status === 200){
      return res.data;
    }
    return {};
  });
}

function destroy(id){
  return (
    axios
      .delete(
        apiEndpointFunction.facilities(id),
        {
          headers: authHeader()
        }
      ).then(res => {
        if(res && res.status === 200){
          return res.data;
        }
      })
  );
}

function getAnimalSire(propertyId){
  return (
    axios
      .get(
        `${apiEndpoint.getAnimalSire}?property_id=${propertyId}`,
        {
          headers: authHeader(),
        }
      ).then(res => {
        if(res && res.status === 200 && res.data.code === '0000'){
          return res.data;
        }
      })
  );
}

function lockOut(data) {
  return axios
    .post(`${apiEndpoint.facilities}/lock-out`, data, {
      headers: authHeader(),
    })
    .then((res) => {
      if (res && res.status === 200) {
        return res.data;
      }
      return {};
    });
}

function updateLockOut(data, historyId) {
  return axios
    .put(`${apiEndpoint.facilities}/update-lock-out/${historyId}`, data, {
      headers: authHeader(),
    })
    .then((res) => {
      if (res && res.status === 200) {
        return res.data;
      }
      return {};
    });
}

function unlock(data) {
  return axios
    .post(`${apiEndpoint.facilities}/unlock-asset`, data, {
      headers: authHeader(),
    })
    .then((res) => {
      if (res && res.status === 200) {
        return res.data;
      }
      return {};
    });
}

// Assign a property user as the responsible/assignee for an asset.
// Backend gates this to owner/admin/safety via
// AttachmentPermissionService::canManageOnProperty, and validates the
// target user belongs to the asset's property.
function assignUser(assetId, userId) {
  return axios
    .put(
      `${apiEndpoint.facilities}/${assetId}/assign-user`,
      { assigned_user_id: userId },
      { headers: authHeader() }
    )
    .then(res => {
      if (res && res.status === 200) {
        return res.data;
      }
      return {};
    });
}

function unassignUser(assetId) {
  return assignUser(assetId, null);
}

// Set or clear an asset's expiry_date. Pass `null` to clear. The
// backend stores ISO ("YYYY-MM-DD") — formatting for AU display is
// the caller's concern.
function setExpiry(assetId, expiryDate) {
  return axios
    .put(
      `${apiEndpoint.facilities}/${assetId}/set-expiry`,
      { expiry_date: expiryDate },
      { headers: authHeader() }
    )
    .then(res => {
      if (res && res.status === 200) {
        return res.data;
      }
      return {};
    });
}
